export enum ExpenseStatusEnum {
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  INFO_NEEDED = 'INFO_NEEDED',
  REJECTED = 'REJECTED',
  CHANGE_MADE = 'CHANGE_MADE',
  PAID = 'PAID',
  PROCESSED = 'PROCESSED',
}

export type ExpenseStatus = `${ExpenseStatusEnum}`;

export enum ExpenseCategoryEnum {
  CLIENT_ENTERTAINMENT = 'CLIENT_ENTERTAINMENT',
  STAFF_ENTERTAINMENT = 'STAFF_ENTERTAINMENT',
  POSTAGE_FREIGHT_COURIER = 'POSTAGE_FREIGHT_COURIER',
  PRINTING = 'PRINTING',
  OFFICE_RENT = 'OFFICE_RENT',
  REPAIR_MAINTANANCE = 'REPAIR_MAINTANANCE',
  TELEPHONE_INTERNET = 'TELEPHONE_INTERNET',
  DOMESTIC_TRAVEL = 'DOMESTIC_TRAVEL',
  INTERNATIONAL_TRAVEL = 'INTERNATIONAL_TRAVEL',
  MILEAGE = 'MILEAGE',
  SUBSISTENCE = 'SUBSISTENCE',
  SUBSCRIPTIONS = 'SUBSCRIPTIONS',
  TRAINING = 'TRAINING',
  OTHERS = 'OTHERS',
  OFFICE_EQUIPMENT = 'OFFICE_EQUIPMENT',
  CLIENT_BILLABLE = 'CLIENT_BILLABLE',
}

export enum ExpensesEditingMethod {
  UPLOAD = 'UPLOAD',
  MANUAL = 'MANUAL',
}

export type ExpenseCategory = `${ExpenseCategoryEnum}`;

export type ExpensesConfig = {
  editingMethod: ExpensesEditingMethod;
};

export type BaseExpense = {
  category: ExpenseCategoryEnum;
  amount: {
    amount: number;
    currency: string;
  };
  status: ExpenseStatusEnum;
  id: string;
  employeeId: string;
  employeeName: string;
  approverId: string;
  approverName: string;
  title: string;
  description: string;
  dateSpent: string;
  statusHistory: {
    statusChangedAt: string;
    statusChangedBy?: string | null;
    requestedChange?: string;
    status: ExpenseStatusEnum;
  }[];
};

export type Expense = BaseExpense & {
  requestedChange: string;
  rejectionReason: string;
  approverSeen: boolean;
  documentId?: string;
  createdAt: string;
  archivedAt?: string | null;
};

export const isExpenseArchived = (expense?: Expense) =>
  !!expense && !!expense.archivedAt;

export const isExpensePending = (expense?: Expense) =>
  !!expense &&
  !isExpenseArchived(expense) &&
  (expense.status === ExpenseStatusEnum.PENDING ||
    expense.status === ExpenseStatusEnum.INFO_NEEDED ||
    expense.status === ExpenseStatusEnum.CHANGE_MADE);

export const isExpensePaid = (expense?: Expense) =>
  !!expense &&
  (expense.status === ExpenseStatusEnum.PAID ||
    expense.status === ExpenseStatusEnum.PROCESSED);

export const getExpensesDates = (statusHistory: Expense['statusHistory']) => {
  const approvalUpdate = statusHistory?.find(
    (update) => update.status === ExpenseStatusEnum.APPROVED,
  );

  const rejectionUpdate = statusHistory?.find(
    (update) => update.status === ExpenseStatusEnum.REJECTED,
  );

  const reportedUpdate = statusHistory?.find(
    (update) => update.status === ExpenseStatusEnum.PROCESSED,
  );

  return {
    approvedDate: approvalUpdate?.statusChangedAt,
    rejectDate: rejectionUpdate?.statusChangedAt,
    reportedDate: reportedUpdate?.statusChangedAt,
  };
};

/**
 * Domain
 * */

export type GetExpensesConfigResponse = ExpensesConfig;

export type GetExpensesResponse = {
  items: BaseExpense[];
  size: number;
  page: number;
  pages: number;
  total: number;
};

export type GetExpenseResponse = Expense;

export type ExportExpensesRequest = {
  approverId: string;
  expenseIds?: string[];
  includeAttachments?: boolean;
  exportFormat?: string;
};

export type AddExpenseRequest = Pick<
  Expense,
  | 'approverId'
  | 'documentId'
  | 'employeeId'
  | 'employeeName'
  | 'title'
  | 'category'
  | 'dateSpent'
  | 'description'
> & {
  amount: string;
  currency: string;
};

export type UpdateExpenseRequest = Partial<Omit<Expense, 'amount'>> & {
  approved?: boolean;
  amount?: number;
  currency?: string;
};

export type BulkUpdateExpensesRequest = {
  expenses: {
    expenseId: string;
    approved?: boolean;
  }[];
};

export type BulkUpdateExpensesResponse = {
  success: boolean;
  error?: Error;
  result?: BaseExpense;
}[];
